import React, { useState } from 'react';
import {
  Configure,
  InfiniteHits,
  InstantSearch,
  SearchBox,
  useInstantSearch,
  type ConfigureProps,
  type InstantSearchProps,
} from 'react-instantsearch';

import { algoliaClient } from '#/lib/algolia';

import { Headline } from './Headline';
import { SearchIcon } from './SearchIcon';

type Props = {
  disableTypoTolerance?: (query: string) => boolean;
  routing?: InstantSearchProps['routing'];
  headline: string;
  description: string;
  searchInputPlaceholder: string;
  indexName: string;
  preListingComponent?: React.ComponentType<{ query: string }>;
  hitComponent: React.ComponentType<{ hit: any }>;
  sidebarComponent?: React.ComponentType;
  classNames?: {
    list?: string;
    item?: string;
    loadMore?: string;
  };
  filters?: string;
  // ID of the featured content that should be hidden in initial search results
  featuredContentId?: string;
};

export function SearchableListing(props: Props) {
  const [query, setQuery] = useState('');

  // If search query is empty, add a filter to exclude the featured content, otherwise show it
  const filters = [
    props.filters,
    props.featuredContentId && !query
      ? `NOT objectID:${props.featuredContentId}`
      : null,
  ]
    .filter(Boolean)
    .join(' AND ');

  return (
    <div className="p-main mb-130 pt-30">
      <InstantSearch
        routing={props.routing}
        indexName={props.indexName}
        searchClient={algoliaClient}
        insights={true}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
        onStateChange={(state) => {
          state.setUiState(state.uiState);
          setQuery(state.uiState[props.indexName]?.query ?? '');
        }}
      >
        <DynamicConfigure
          filters={filters}
          disableTypoTolerance={props.disableTypoTolerance}
          query={query}
        />
        <div className="grid-main">
          <div className="col-span-full mb-20 l:col-span-4 l:mb-0">
            <div className="l:sticky l:top-130 l:max-w-230 mx-auto">
              <div className="mx-auto m:max-w-345 text-center l:text-left">
                <Headline value={props.headline} className="mb-12 l:mb-20" />
                <p className="mb-30 text-balance text-16 text-grayscale-06">
                  {props.description}
                </p>
                <SearchBox
                  className="m:mb-32"
                  placeholder={props.searchInputPlaceholder}
                  submitIconComponent={() => (
                    <SearchIcon className="w-18 text-grayscale-04" />
                  )}
                  resetIconComponent={() => null}
                />
              </div>
              {props?.sidebarComponent && <props.sidebarComponent />}
            </div>
          </div>
          <div className="col-span-full l:col-span-8">
            <NoResultsBoundary fallback={<NoResults />}>
              {props.preListingComponent && (
                <props.preListingComponent query={query} />
              )}
              <InfiniteHits
                classNames={{
                  loadMore:
                    'block mx-auto mt-50 m:mt-60 font-[440] leading-100 -tracking-1 transition-colors rounded-10 border-1 border-current px-16 py-12 l:px-16 l:py-13 l:text-14 hover:bg-charcoal hover:text-cloud bg-cloud text-charcoal disabled:hidden',
                  ...props.classNames,
                }}
                hitComponent={props.hitComponent}
                showPrevious={false}
                translations={{
                  showMoreButtonText: 'View more',
                }}
              />
            </NoResultsBoundary>
          </div>
        </div>
      </InstantSearch>
    </div>
  );
}

function DynamicConfigure({
  filters,
  configure,
  disableTypoTolerance,
  query,
}: {
  filters: string;
  configure?: ConfigureProps;
  disableTypoTolerance?: (query: string) => boolean;
  query: string;
}) {
  // Compute typoTolerance based on the current query
  const typoTolerance = disableTypoTolerance
    ? !disableTypoTolerance(query)
    : undefined;

  return (
    <Configure
      hitsPerPage={12}
      filters={filters}
      {...configure}
      {...(typoTolerance !== undefined && { typoTolerance })}
    />
  );
}

function NoResultsBoundary({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback: React.ReactNode;
}) {
  const { results } = useInstantSearch();

  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned.
  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return children;
}

function NoResults() {
  const { indexUiState } = useInstantSearch();

  return (
    <div>
      <p>
        No results for <q>{indexUiState.query}</q>.
      </p>
    </div>
  );
}
